import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { MEDIA, SPECTORA_URL } from "@/lib/media";

const line1 = ["Inspect", "the"];
const line2 = ["Uninspected"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const word = {
    hidden: { y: "110%" },
    show: (i) => ({
      y: "0%",
      transition: { duration: 1, delay: 0.3 + i * 0.09, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ scale }} className="absolute inset-0">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src={MEDIA.heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-ink/10 to-ink" />
        <div className="absolute inset-0 bg-ink/10" />
      </motion.div>

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="h-px w-12 bg-gold" />
          <span className="font-serif italic text-gold tracking-[0.25em] text-sm md:text-base">
            Commercial and Residential Home Inspections
          </span>
        </motion.div>

        <h1 className="font-display uppercase text-cream leading-[0.85] tracking-tight text-[10vw] md:text-[9vw] lg:text-[8.5rem]">
          <span className="block overflow-hidden">
            <span className="flex gap-[0.25em]">
              {line1.map((w, i) => (
                <span key={w} className="overflow-hidden inline-block">
                  <motion.span variants={word} initial="hidden" animate="show" custom={i} className="inline-block">
                    {w}
                  </motion.span>
                </span>
              ))}
            </span>
          </span>
          <span className="block overflow-hidden">
            <span className="flex gap-[0.25em]">
              {line2.map((w, i) => (
                <span key={w} className="overflow-hidden inline-block">
                  <motion.span
                    variants={word}
                    initial="hidden"
                    animate="show"
                    custom={i + line1.length}
                    className={`inline-block ${i === 0 ? "text-gold" : ""}`}
                  >
                    {w}
                  </motion.span>
                </span>
              ))}
            </span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-8 max-w-xl text-cream/70 text-base md:text-lg leading-relaxed"
        >
          From the roofline to the foundation, Home Point Inspection, Inc delivers meticulous, technology-driven
          inspections throughout Georgia — so you know exactly what you're buying.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href={SPECTORA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 font-display uppercase tracking-[0.15em] text-sm hover:bg-gold-hover transition-colors duration-300"
            data-testid="hero-cta-button"
          >
            Book An Inspection
            <ArrowDown className="rotate-[-90deg] group-hover:translate-x-1 transition-transform" size={18} />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-cream/50"
      >
        <span className="font-display uppercase tracking-[0.3em] text-[10px]">Scroll To Explore</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
